/**
 * Verde Esmeralda — Crea tu caja regalo personalizada
 *
 * Multi-caja builder. Each "pack" the user is assembling has:
 *   - a variety: '' | 'gema' | 'imagine'
 *   - a bottles map: { '<product-handle>': <qty> }, sum <= 3
 *
 * Architecture (post 3-col refactor):
 *   - The page renders ONE editor at section level (variety col + bottles
 *     col). Only the ACTIVE pack drives that editor. JS updates the
 *     DOM in place when the active pack changes.
 *   - The sidebar lists every pack as a clickable card; on mobile the
 *     whole sidebar becomes a bottom-sheet drawer triggered by a fixed
 *     bar at the foot of the viewport.
 *
 * Cart payload (unchanged): every pack with bottles_total === 3 becomes
 *   - 1 cart line for the caja product (box, €30)
 *   - N cart lines for the bottles inside, each at its normal price
 * All lines share a `_ve_pack_id` line-item property so the cart can
 * group them visually if desired. A human-readable `Contenido` /
 * `Caja` property is also added so the customer sees what's grouped.
 *
 * No external deps.
 */
(function () {
  'use strict';

  const ROOT_SEL = '[data-create-pack]';
  const root = document.querySelector(ROOT_SEL);
  if (!root) return;

  const config = {
    boxes: {
      gema:    JSON.parse(root.dataset.boxGema    || 'null'),
      imagine: JSON.parse(root.dataset.boxImagine || 'null'),
    },
    bottles: JSON.parse(root.dataset.bottles || '{}'),
    moneyFormat: root.dataset.moneyFormat || '€{{amount}}',
    checkoutUrl: root.dataset.checkoutUrl || '/cart',
    maxPacks: parseInt(root.dataset.maxPacks || '5', 10),
    labels: {
      caja: { gema: 'Caja Gema', imagine: 'Caja Imagine' },
      varietyName: { gema: 'Gema', imagine: 'Imagine' },
    },
  };

  // Cached DOM handles for the section-level editor + sidebar.
  const editor = {
    pack: root.querySelector('[data-pack]'),                  // <section data-pack>
    varietyButtons: Array.from(root.querySelectorAll('[data-box-variety]')),
    lockNote: root.querySelector('[data-variety-lock-note]'),
    infoLink: root.querySelector('[data-info-open]'),
    infoLinkLabel: root.querySelector('[data-info-link-label]'),
    aboutTemplates: {
      gema:    root.querySelector('[data-about-content-gema]'),
      imagine: root.querySelector('[data-about-content-imagine]'),
    },
    infoDialog: root.querySelector('[data-info-dialog]'),
    infoModalEyebrow: root.querySelector('[data-info-modal-eyebrow]'),
    infoModalTitle: root.querySelector('[data-info-modal-title]'),
    infoModalBody: root.querySelector('[data-info-modal-body]'),
    counter: root.querySelector('[data-counter]'),
    counterGrid: root.querySelector('[data-counter-grid]'),
    indexNum: root.querySelector('[data-pack-index-num]'),
    completePill: root.querySelector('[data-complete-pill]'),
    bottlesEmpty: root.querySelector('[data-bottles-empty]'),
    bottlesGrid: root.querySelector('[data-bottles]'),
    bottleCards: Array.from(root.querySelectorAll('[data-pack-bottle]')),
    completeBanner: root.querySelector('[data-complete-banner]'),
  };

  const sidebar = {
    wrap:      root.querySelector('[data-sidebar-wrap]'),
    listEl:    root.querySelector('[data-pack-list]'),
    listWrap:  root.querySelector('[data-list-wrap]'),
    countEl:   root.querySelector('[data-pack-count]'),
    subtotal:  root.querySelector('[data-subtotal]'),
    notice:    root.querySelector('[data-notice]'),
    cta:       root.querySelector('[data-checkout]'),
    newPack:   root.querySelectorAll('[data-add-pack]'),
  };

  const mobile = {
    bar:       root.querySelector('[data-mobile-bar]'),
    backdrop:  root.querySelector('[data-mobile-backdrop]'),
    count:     root.querySelector('[data-mobile-count]'),
    total:     root.querySelector('[data-mobile-total]'),
    cta:       root.querySelector('[data-mobile-cta]'),
  };

  /* ──── State + localStorage persistence ─────────────────────
     Survives page reloads and PDP round-trips. Stored as a single
     JSON blob under STORAGE_KEY. Validated on load against the
     current bottle catalog — if a stored handle no longer exists
     in `config.bottles`, that entry is dropped silently. */
  const STORAGE_KEY = 've-create-pack-state-v1';

  const state = {
    packs: [],          // [{ id, variety, bottles: {handle: qty} }]
    activePackId: null,
    submitting: false,
  };

  function saveState() {
    try {
      const payload = {
        packs: state.packs.map(p => ({
          id: p.id,
          variety: p.variety,
          bottles: p.bottles,
        })),
        activePackId: state.activePackId,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // Quota exceeded, private browsing, etc. — silent fallthrough.
    }
  }
  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !Array.isArray(data.packs) || data.packs.length === 0) return null;

      const cleaned = data.packs.map(p => {
        if (!p || typeof p !== 'object') return null;
        const variety = p.variety === 'gema' || p.variety === 'imagine' ? p.variety : '';
        const bottles = {};
        if (variety && p.bottles && typeof p.bottles === 'object') {
          Object.entries(p.bottles).forEach(([h, q]) => {
            const b = config.bottles[h];
            const qty = Number(q) | 0;
            if (b && b.variety === variety && qty > 0 && qty <= 3) {
              bottles[h] = qty;
            }
          });
          // Cap total at 3 — discard any overflow
          let total = Object.values(bottles).reduce((s, x) => s + x, 0);
          if (total > 3) {
            // Trim from the last entry until total <= 3
            const entries = Object.entries(bottles);
            while (total > 3 && entries.length) {
              const [h] = entries[entries.length - 1];
              bottles[h]--;
              total--;
              if (bottles[h] <= 0) {
                delete bottles[h];
                entries.pop();
              }
            }
          }
        }
        return {
          id: typeof p.id === 'string' ? p.id : newPackId(),
          variety,
          bottles,
        };
      }).filter(Boolean);

      if (cleaned.length === 0) return null;

      const wantedActive = data.activePackId;
      const activeId = (wantedActive && cleaned.some(p => p.id === wantedActive))
        ? wantedActive
        : cleaned[0].id;

      return { packs: cleaned, activePackId: activeId };
    } catch (e) {
      return null;
    }
  }
  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function newPackId() {
    return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }
  function packTotal(p) {
    return Object.values(p.bottles).reduce((s, q) => s + q, 0);
  }
  function packComplete(p) {
    return p.variety && packTotal(p) === 3;
  }
  function activePack() {
    return state.packs.find(p => p.id === state.activePackId) || null;
  }

  function setActive(id) {
    if (state.activePackId === id) return;
    state.activePackId = id;
    render();
    saveState();
    if (isMobile()) closeDrawer();
  }
  function addPack() {
    if (state.packs.length >= config.maxPacks) return;
    const p = { id: newPackId(), variety: '', bottles: {} };
    state.packs.push(p);
    state.activePackId = p.id;
    render();
    saveState();
    if (isMobile()) closeDrawer();
  }
  function removePack(id) {
    // Need at least one pack at all times — but ANY pack (including
    // the first) can be removed when there's more than one.
    if (state.packs.length <= 1) return;
    const idx = state.packs.findIndex(p => p.id === id);
    if (idx === -1) return;
    const wasActive = state.activePackId === id;
    state.packs.splice(idx, 1);
    if (wasActive) {
      // Activate the pack now sitting at this index (the next one
      // shifted in), or the previous pack if we removed the last.
      const newActive = state.packs[idx] || state.packs[idx - 1];
      if (newActive) state.activePackId = newActive.id;
    }
    render();
    saveState();
  }

  function setVariety(v) {
    const p = activePack();
    if (!p) return;
    if (packTotal(p) > 0) return; // locked
    if (p.variety === v) return;
    p.variety = v;
    p.bottles = {};
    render();
    saveState();
  }
  function updateBottle(handle, delta) {
    const p = activePack();
    if (!p) return;
    const current = p.bottles[handle] || 0;
    const total = packTotal(p);
    if (delta > 0 && total >= 3) return;
    const next = current + delta;
    if (next <= 0) {
      delete p.bottles[handle];
    } else {
      p.bottles[handle] = next;
    }
    render();
    saveState();
  }

  /* ──── Money formatter ──── */
  function formatMoney(cents) {
    const value = cents / 100;
    const dot   = value.toFixed(2);
    const comma = dot.replace('.', ',');
    const noDec = Math.round(value).toString();
    return config.moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, dot.replace('.', "'"))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount\s*\}\}/g, dot);
  }
  function escapeAttr(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ──── Render: editor (variety col + bottles col) ───── */
  function renderEditor() {
    const p = activePack();
    const variety = p ? p.variety : '';
    const total = p ? packTotal(p) : 0;
    const complete = p ? packComplete(p) : false;

    if (editor.pack) {
      editor.pack.dataset.variety = variety;
      editor.pack.classList.toggle('is-complete', complete);
    }

    // Variety buttons: aria-pressed + aria-disabled state
    editor.varietyButtons.forEach((btn) => {
      const v = btn.dataset.boxVariety;
      const isSelected = variety === v;
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      // Lock the OTHER variety when bottles already exist
      const locked = total > 0 && !isSelected;
      btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
    });
    if (editor.lockNote) editor.lockNote.hidden = total === 0;

    // "Más info" link — show only when the active variety has
    // configured about content. The modal stays in DOM but hidden;
    // clicking the link populates and shows it.
    if (editor.infoLink) {
      const tpl = variety ? editor.aboutTemplates[variety] : null;
      const hasContent = tpl && tpl.innerHTML.trim().length > 0;
      editor.infoLink.hidden = !(variety && hasContent);
      if (editor.infoLinkLabel && variety) {
        editor.infoLinkLabel.textContent =
          'Más info sobre la caja ' + config.labels.varietyName[variety];
      }
    }

    // Pack index + counters
    const idx = p ? state.packs.findIndex(x => x.id === p.id) : 0;
    if (editor.indexNum) editor.indexNum.textContent = String(idx + 1);
    if (editor.counter) editor.counter.textContent = String(total);
    if (editor.counterGrid) editor.counterGrid.textContent = String(total);
    if (editor.completePill) editor.completePill.hidden = !complete;
    if (editor.completeBanner) editor.completeBanner.hidden = !complete;

    // Bottles grid vs empty state
    if (editor.bottlesEmpty) editor.bottlesEmpty.hidden = !!variety;
    if (editor.bottlesGrid)  editor.bottlesGrid.hidden  = !variety;

    // Per-card state — ALWAYS rebuild from the active pack's bottles,
    // regardless of whether the grid is currently visible. This avoids
    // stale DOM state when the user switches between packs (e.g.
    // builds caja 1 with 3 bottles, then opens caja 2 — caja 2 must
    // start at qty 0 for every bottle, not inherit caja 1's qty).
    editor.bottleCards.forEach((card) => {
      const handle = card.dataset.bottleHandle;
      const cardVariety = card.dataset.bottleVariety;

      // Variety filter — hide cards from the other variety. Hide all
      // cards when no variety is chosen (the empty state shows
      // instead).
      const isVisible = variety && cardVariety === variety;
      card.classList.toggle('ve-pack-bottle--hidden', !isVisible);

      const qty = p ? (p.bottles[handle] || 0) : 0;
      card.classList.toggle('is-selected', qty > 0);

      const qtyEl   = card.querySelector('[data-bottle-qty]');
      const minus   = card.querySelector('[data-bottle-minus]');
      const plus    = card.querySelector('[data-bottle-plus]');
      const addBtn  = card.querySelector('[data-bottle-add]');
      const stepper = card.querySelector('.ve-builder-card__stepper');

      if (qtyEl) qtyEl.textContent = String(qty);
      if (minus) minus.disabled = qty === 0;
      if (plus)  plus.disabled  = total >= 3;
      // Swap "Sumar al pedido" button for the stepper when qty > 0
      if (addBtn)  addBtn.hidden  = qty > 0;
      if (stepper) stepper.hidden = qty === 0;
    });
  }

  /* ──── Render: sidebar (pack list + totals + CTA + mobile bar) ──── */
  function renderSidebar() {
    if (!sidebar.listEl) return;
    sidebar.listEl.innerHTML = '';

    let subtotal = 0;
    let completedPacks = 0;

    state.packs.forEach((p, i) => {
      const isActive = p.id === state.activePackId;
      const isComplete = packComplete(p);
      const total = packTotal(p);

      if (isComplete) {
        completedPacks++;
        const box = config.boxes[p.variety];
        if (box) subtotal += box.price;
        Object.entries(p.bottles).forEach(([handle, qty]) => {
          const b = config.bottles[handle];
          if (b) subtotal += b.price * qty;
        });
      }

      // Bottle thumbnails (pad to 3 slots)
      const flat = [];
      Object.entries(p.bottles).forEach(([handle, qty]) => {
        for (let k = 0; k < qty; k++) flat.push(handle);
      });
      const slots = flat.slice(0, 3);
      while (slots.length < 3) slots.push(null);
      const thumbsHtml = slots.map((h) => {
        if (!h) {
          return '<span class="ve-create-pack__pack-thumb is-empty" aria-hidden="true"></span>';
        }
        const b = config.bottles[h];
        if (b && b.image) {
          return `<span class="ve-create-pack__pack-thumb"><img src="${escapeAttr(b.image)}" alt="${escapeAttr(b.title)}" loading="lazy"/></span>`;
        }
        return '<span class="ve-create-pack__pack-thumb is-empty" aria-hidden="true"></span>';
      }).join('');

      const statusIcon = isComplete
        ? '<span class="ve-create-pack__pack-status is-complete" aria-label="Caja completa">✓</span>'
        : isActive
        ? '<span class="ve-create-pack__pack-status is-active" aria-label="Caja activa">●</span>'
        : `<span class="ve-create-pack__pack-status is-empty">${i + 1}</span>`;

      const varietyLabel = p.variety
        ? config.labels.varietyName[p.variety] + (total > 0 && !isComplete ? ' · ' + total + '/3' : '')
        : 'Sin elegir';

      const li = document.createElement('li');
      li.className = 've-create-pack__pack-item';
      li.innerHTML = `
        <button type="button" class="ve-create-pack__pack-card${isActive ? ' is-active' : ''}${isComplete ? ' is-complete' : ''}" data-pack-switch="${p.id}">
          <div class="ve-create-pack__pack-card-head">
            ${statusIcon}
            <div class="ve-create-pack__pack-card-meta">
              <strong>Caja ${i + 1}</strong>
              <span>${varietyLabel}</span>
            </div>
          </div>
          <div class="ve-create-pack__pack-card-thumbs" aria-hidden="true">
            ${thumbsHtml}
          </div>
        </button>
        ${state.packs.length > 1 ? `<button type="button" class="ve-create-pack__pack-remove" data-pack-remove-id="${p.id}" aria-label="Eliminar caja ${i + 1}">×</button>` : ''}
      `;
      sidebar.listEl.appendChild(li);
    });

    if (sidebar.subtotal) sidebar.subtotal.textContent = formatMoney(subtotal);
    if (sidebar.countEl) {
      sidebar.countEl.textContent = completedPacks === 1
        ? '1 caja completa'
        : `${completedPacks} cajas completas`;
    }
    const allComplete = completedPacks > 0 &&
      !state.packs.some(pk => !packComplete(pk));
    if (sidebar.cta) sidebar.cta.disabled = !allComplete || state.submitting;

    // Notice: surfaces "complete to continue" when something is in progress
    if (sidebar.notice) {
      const anyInProgress = state.packs.some(pk => pk.variety && !packComplete(pk));
      if (anyInProgress && !allComplete) {
        sidebar.notice.hidden = false;
        sidebar.notice.textContent = 'Completa 3 botellas en cada caja para continuar.';
      } else {
        sidebar.notice.hidden = true;
      }
    }

    // "+ Añadir nueva caja" disabled when max reached
    if (sidebar.newPack && sidebar.newPack.length) {
      const atMax = state.packs.length >= config.maxPacks;
      sidebar.newPack.forEach((btn) => {
        btn.disabled = atMax;
      });
    }

    // Mobile bar mirror
    renderMobileBar(completedPacks, allComplete, subtotal);

    updateScrollFades();
  }

  function renderMobileBar(completedPacks, allComplete, subtotal) {
    if (!mobile.bar) return;
    const totalPacks = state.packs.length;
    if (mobile.count) {
      const inProgress = totalPacks - completedPacks;
      let line;
      if (completedPacks === 0 && inProgress > 0) {
        line = inProgress === 1 ? '1 caja en progreso' : `${inProgress} cajas en progreso`;
      } else if (completedPacks > 0 && inProgress > 0) {
        line = `${completedPacks} lista${completedPacks > 1 ? 's' : ''} · ${inProgress} en progreso`;
      } else if (completedPacks > 0) {
        line = completedPacks === 1 ? '1 caja lista' : `${completedPacks} cajas listas`;
      } else {
        line = 'Tu pedido';
      }
      mobile.count.textContent = line;
    }
    if (mobile.total) mobile.total.textContent = formatMoney(subtotal);
    if (mobile.cta) mobile.cta.disabled = !allComplete || state.submitting;
  }

  function updateScrollFades() {
    if (!sidebar.listWrap || !sidebar.listEl) return;
    requestAnimationFrame(() => {
      const canUp   = sidebar.listEl.scrollTop > 2;
      const canDown = sidebar.listEl.scrollHeight - sidebar.listEl.clientHeight - sidebar.listEl.scrollTop > 2;
      sidebar.listWrap.dataset.canScrollUp   = canUp ? 'true' : 'false';
      sidebar.listWrap.dataset.canScrollDown = canDown ? 'true' : 'false';
    });
  }

  function render() {
    renderEditor();
    renderSidebar();
  }

  /* ──── Mobile drawer ─────────────────────────────── */
  function isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }
  function openDrawer() {
    if (!sidebar.wrap || !mobile.backdrop) return;
    sidebar.wrap.classList.add('is-open');
    mobile.backdrop.classList.add('is-open');
    lockBodyScroll();
  }
  function closeDrawer() {
    if (!sidebar.wrap || !mobile.backdrop) return;
    sidebar.wrap.classList.remove('is-open');
    mobile.backdrop.classList.remove('is-open');
    // Only unlock if the info dialog isn't also open (native <dialog>
    // manages its own scroll lock when .open is true)
    if (!editor.infoDialog || !editor.infoDialog.open) {
      unlockBodyScroll();
    }
  }

  /* ──── Body scroll lock — compensate scrollbar to prevent shift ─────
     When `overflow: hidden` is set on body, the desktop scrollbar
     disappears and the page content jumps ~15-17px to the right (the
     scrollbar's reserved width). We compensate by adding the same
     amount of padding-right BEFORE locking. Mobile scrollbars overlay
     and don't take width, so the calc returns 0 there and we no-op. */
  function lockBodyScroll() {
    if (document.body.dataset.veScrollLocked === '1') return;
    const sw = window.innerWidth - document.documentElement.clientWidth;
    if (sw > 0) {
      // Stash the previous inline padding-right (if any) so we can
      // restore it exactly on unlock.
      document.body.dataset.vePrevPaddingRight = document.body.style.paddingRight || '';
      document.body.style.paddingRight = sw + 'px';
    }
    document.body.dataset.vePrevOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    document.body.dataset.veScrollLocked = '1';
  }
  function unlockBodyScroll() {
    if (document.body.dataset.veScrollLocked !== '1') return;
    document.body.style.overflow = document.body.dataset.vePrevOverflow || '';
    document.body.style.paddingRight = document.body.dataset.vePrevPaddingRight || '';
    delete document.body.dataset.veScrollLocked;
    delete document.body.dataset.vePrevOverflow;
    delete document.body.dataset.vePrevPaddingRight;
  }

  /* ──── Info modal — native <dialog> ───────────────────────────
     Mirrors the PDP nutritional modal pattern. The browser handles
     the ::backdrop, ESC key, focus trap and body scroll lock
     automatically when we call showModal(). close() closes it. */
  function openInfoModal() {
    const p = activePack();
    if (!p || !p.variety) return;
    const tpl = editor.aboutTemplates[p.variety];
    if (!editor.infoDialog || !tpl || !tpl.innerHTML.trim()) return;
    if (editor.infoModalTitle) {
      editor.infoModalTitle.textContent =
        'Sobre la caja ' + config.labels.varietyName[p.variety];
    }
    if (editor.infoModalBody) {
      editor.infoModalBody.innerHTML = tpl.innerHTML;
    }
    if (typeof editor.infoDialog.showModal === 'function') {
      editor.infoDialog.showModal();
    } else {
      // Legacy browsers without <dialog> support — graceful fallback
      editor.infoDialog.setAttribute('open', '');
    }
  }
  function closeInfoModal() {
    if (!editor.infoDialog) return;
    if (typeof editor.infoDialog.close === 'function') {
      editor.infoDialog.close();
    } else {
      editor.infoDialog.removeAttribute('open');
    }
  }

  /* ──── Cart submission ──── */
  async function submitToCart() {
    if (state.submitting) return;
    state.submitting = true;

    // Lock both desktop and mobile CTAs while in flight
    const ctas = [sidebar.cta, mobile.cta].filter(Boolean);
    ctas.forEach((btn) => {
      btn.dataset.originalLabel = btn.textContent;
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.textContent = 'Añadiendo...';
    });

    const items = [];
    state.packs.forEach((p) => {
      if (!packComplete(p)) return;
      const box = config.boxes[p.variety];
      if (!box || !box.variantId) return;
      const packId = p.id;
      const contenido = Object.entries(p.bottles)
        .map(([h, q]) => `${(config.bottles[h]?.title || h)} ×${q}`)
        .join(', ');
      items.push({
        id: box.variantId,
        quantity: 1,
        properties: {
          _ve_pack_id: packId,
          _ve_pack_role: 'box',
          Contenido: contenido,
        },
      });
      Object.entries(p.bottles).forEach(([handle, qty]) => {
        const b = config.bottles[handle];
        if (!b || !b.variantId) return;
        items.push({
          id: b.variantId,
          quantity: qty,
          properties: {
            _ve_pack_id: packId,
            _ve_pack_role: 'bottle',
            Caja: config.labels.caja[p.variety],
          },
        });
      });
    });

    if (items.length === 0) {
      state.submitting = false;
      ctas.forEach((btn) => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        btn.textContent = btn.dataset.originalLabel;
      });
      return;
    }

    try {
      const cartDrawerEl = document.querySelector('cart-drawer');
      /* Ask Shopify to render the cart sections inline with the add
         so the response includes HTML that already reflects the items
         we just added (no eventual-consistency window between the add
         and the render). Then fetch /cart.js once to get item_count +
         the full cart state — renderContents uses `item_count` to
         toggle the is-empty class on <cart-drawer> and
         <cart-drawer-items>; without it, the drawer keeps whatever
         is-empty state it had at initial page load (typically true,
         which hides the items list via CSS). */
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const addResponse = await res.json();

      const cartData = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const cartResponse = {
        ...cartData,
        sections: addResponse.sections || {},
      };

      // Cart accepted. We DON'T redirect to /cart so the customer can
      // keep building another box if they want. Instead:
      //   1. Wipe persisted + in-memory state so the builder shows a
      //      fresh empty pack (otherwise the next submit would re-add
      //      the same items, double-charging the cart).
      //   2. Re-render the editor + sidebar.
      //   3. Surface a brief success notice in the sidebar.
      //   4. Publish PUB_SUB_EVENTS.cartUpdate so the theme's cart icon
      //      bubble + drawer rerender from the latest cart state. This
      //      is the same event the PDP "Add to cart" emits (see
      //      assets/product-form.js).
      clearState();
      state.packs = [];
      state.activePackId = null;
      // Re-seed with a fresh empty pack so the UI doesn't render blank.
      state.packs.push({ id: newPackId(), variety: null, bottles: {} });
      state.activePackId = state.packs[0].id;

      state.submitting = false;
      ctas.forEach((btn) => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        btn.textContent = btn.dataset.originalLabel;
      });

      render();

      if (sidebar.notice) {
        sidebar.notice.hidden = false;
        sidebar.notice.classList.add('is-success');
        sidebar.notice.textContent = '¡Tu pedido se agregó al carrito!';
        window.setTimeout(() => {
          if (!sidebar.notice) return;
          sidebar.notice.hidden = true;
          sidebar.notice.classList.remove('is-success');
        }, 5000);
      }

      /* Render the cart drawer with the merged state. We do NOT use
         Dawn's reload() because it calls renderContents({sections:...}, true)
         WITHOUT item_count — and renderContents only toggles the is-empty
         class on <cart-drawer> when item_count is a number. The outer
         <cart-drawer> element keeps the is-empty class from initial page
         load (cart was empty), CSS rule `cart-drawer.is-empty .drawer__inner
         { display: grid; grid-template-rows: 1fr; align-items: center }`
         then breaks the layout and hides the items. Passing the merged
         state (cart-from-/cart.js + sections-from-/cart/add.js) means
         item_count is a number and is-empty is correctly toggled off. */
      if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
        cartDrawerEl.renderContents(cartResponse, true);
      }

      /* Notify other listeners so they re-hydrate from the latest cart.
         We pass `cartData` (full cart from /cart.js) so subscribers that
         read item_count / items work without another fetch. */
      try {
        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'create-pack',
          cartData: cartResponse,
        });
      } catch (pubErr) {
        console.warn('[create-pack] could not publish cartUpdate', pubErr);
      }
    } catch (err) {
      console.error('[create-pack] cart add error', err);
      state.submitting = false;
      ctas.forEach((btn) => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        btn.textContent = btn.dataset.originalLabel;
      });
      if (sidebar.notice) {
        sidebar.notice.hidden = false;
        sidebar.notice.textContent =
          'No pudimos añadir tu pedido al carrito. Intentá de nuevo o contactanos.';
      }
    }
  }

  /* ──── Events (delegated at section root) ──── */
  root.addEventListener('click', (e) => {
    // Drawer open/close
    if (e.target.closest('[data-open-drawer]'))  { openDrawer();  return; }
    if (e.target.closest('[data-close-drawer]')) { closeDrawer(); return; }

    // Info modal
    if (e.target.closest('[data-info-open]')) { openInfoModal(); return; }
    // Modal close handlers are wired directly to the dialog/close
    // button at init (native <dialog> lives in the DOM at any depth
    // but doesn't bubble its internal events the same way as a div).

    // Variety toggle
    const v = e.target.closest('[data-box-variety]');
    if (v) {
      if (v.getAttribute('aria-disabled') === 'true') return;
      setVariety(v.dataset.boxVariety);
      return;
    }

    // Bottle +/- (event-delegated; reuse builder-card stepper buttons)
    const plus = e.target.closest('[data-bottle-plus]');
    if (plus && !plus.disabled) {
      const card = plus.closest('[data-pack-bottle]');
      if (card) updateBottle(card.dataset.bottleHandle, +1);
      return;
    }
    const minus = e.target.closest('[data-bottle-minus]');
    if (minus && !minus.disabled) {
      const card = minus.closest('[data-pack-bottle]');
      if (card) updateBottle(card.dataset.bottleHandle, -1);
      return;
    }
    const addBtn = e.target.closest('[data-bottle-add]');
    if (addBtn && !addBtn.disabled) {
      const card = addBtn.closest('[data-pack-bottle]');
      if (card) updateBottle(card.dataset.bottleHandle, +1);
      return;
    }

    // Sidebar: switch active pack
    const sw = e.target.closest('[data-pack-switch]');
    if (sw) { setActive(sw.dataset.packSwitch); return; }

    // Sidebar: remove pack
    const rm = e.target.closest('[data-pack-remove-id]');
    if (rm) { e.stopPropagation(); removePack(rm.dataset.packRemoveId); return; }

    // Add new pack (sidebar + complete-banner CTAs share data-add-pack)
    const ad = e.target.closest('[data-add-pack]');
    if (ad && !ad.disabled) { addPack(); return; }

    // Checkout
    if (e.target.closest('[data-checkout]') || e.target.closest('[data-mobile-cta]')) {
      submitToCart();
      return;
    }

    // Backdrop tap closes drawer
    if (e.target.closest('[data-mobile-backdrop]')) { closeDrawer(); return; }
  });

  // ESC closes mobile drawer. Native <dialog> already handles ESC
  // for the info modal automatically, so we only act on the drawer
  // (and skip if the modal is open — ESC will go to the dialog).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (editor.infoDialog && editor.infoDialog.open) return;
    closeDrawer();
  });
  // Live update of scroll-fade indicators
  if (sidebar.listEl) {
    sidebar.listEl.addEventListener('scroll', updateScrollFades, { passive: true });
  }

  /* ──── Init ─────────────────────────────────────────── */
  function init() {
    // Native <dialog>: wire the close button + backdrop-click-to-close.
    // Browser handles ESC, focus trap and scroll lock automatically.
    if (editor.infoDialog) {
      editor.infoDialog.addEventListener('click', (e) => {
        // Click on the <dialog> background (outside the content) closes
        if (e.target === editor.infoDialog) closeInfoModal();
      });
      editor.infoDialog.querySelectorAll('[data-info-modal-close]').forEach((btn) => {
        btn.addEventListener('click', closeInfoModal);
      });
    }

    const restored = loadState();
    if (restored) {
      state.packs = restored.packs;
      state.activePackId = restored.activePackId;
      render();
    } else {
      addPack();
    }
  }
  init();
})();
